import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { LanguageCode } from '../types/entryRules.types';
import { useEntryRules } from '../hooks/useEntryRules';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { EntryRulesHeader } from '../components/EntryRulesHeader/EntryRulesHeader';
import { LanguageTabs } from '../components/LanguageTabs/LanguageTabs';
import { EntryRuleCard } from '../components/EntryRuleCard/EntryRuleCard';
import { DeleteRuleModal } from '../components/DeleteRuleModal/DeleteRuleModal';

export function EntryRulesPage() {
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>('ru');
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; localId: string | null }>({
    open: false,
    localId: null,
  });

  const {
    cards,
    usedCountryCodes,
    isLoading,
    loadRules,
    addCard,
    updateCardData,
    saveCard,
    deleteCard,
    toggleExpand,
    hasUnsavedCards,
    canAddRule,
  } = useEntryRules();

  const newCardRef = useRef<HTMLDivElement | null>(null);

  useUnsavedGuard(hasUnsavedCards);

  useEffect(() => {
    loadRules(activeLanguage);
  }, [activeLanguage, loadRules]);

  const handleAddCard = () => {
    addCard();
    requestAnimationFrame(() => {
      newCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSaveCard = useCallback(
    (localId: string) => saveCard(localId, activeLanguage),
    [saveCard, activeLanguage]
  );

  const handleDeleteRequest = useCallback(
    (localId: string) => setDeleteModal({ open: true, localId }),
    []
  );

  const handleDeleteConfirm = async () => {
    if (!deleteModal.localId) return;
    try {
      await deleteCard(deleteModal.localId, activeLanguage);
      setDeleteModal({ open: false, localId: null });
    } catch {
      toast.error('Не удалось удалить правило въезда');
    }
  };

  const handleLanguageChange = (lang: LanguageCode) => {
    setActiveLanguage(lang);
  };

  return (
    <div className="max-w-[860px] mx-auto flex flex-col gap-6 pb-8">
      <EntryRulesHeader onAdd={handleAddCard} canAdd={canAddRule} />

      <LanguageTabs
        active={activeLanguage}
        onChange={handleLanguageChange}
        disabled={hasUnsavedCards}
      />

      <div className="flex flex-col gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </>
        ) : (
          cards.map((card, i) => (
            <EntryRuleCard
              key={card.localId}
              ref={card.status === 'new' && card.serverId === null ? newCardRef : undefined}
              card={card}
              index={i + 1}
              lang={activeLanguage}
              usedCountryCodes={usedCountryCodes}
              onUpdate={updateCardData}
              onSave={handleSaveCard}
              onDeleteRequest={handleDeleteRequest}
              onToggle={toggleExpand}
            />
          ))
        )}
      </div>

      <button
        onClick={handleAddCard}
        disabled={!canAddRule}
        className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 flex items-center justify-center gap-2 text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-500"
      >
        <Plus className="h-4 w-4" />
        Добавить правило
      </button>

      <DeleteRuleModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal((prev) => ({ ...prev, open }))}
        onConfirm={handleDeleteConfirm}
        lang={activeLanguage}
      />
    </div>
  );
}
